import { Accordion, AccordionSummary, Divider, Paper, Stack, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import { Card, CardContent, CardTitle, Label, ProductOverviewPage, Switch, Skeleton } from "randmarcomps";
import * as React from "react";
import { useParams } from "react-router-dom";
import MarginsGrid from '../components/grids/MarginsGrid';
import EditDistributionInformation from "../components/products/EditDistributionInformation";
import EditShopifyListing from '../components/products/EditShopifyListing';
import InstantRebatesGrid from "../components/products/InstantRebatesGrid";
import ProductActions from "../components/products/ProductActions";
import { DocumentHeader, StyledAccordionDetails, StyledAccordionTitle } from "../components/util/StyledComponents";
import OpenOrdersSummary from '../componentsCore/cards/OpenOrdersSummary';
import withManufacturer from "../contexts/WithManufacturer";
import ExpandPanelIcon from "../icons/ExpandPanelIcon";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const Product: React.FC<IProps> = (props) => {
  const { randmarSKU } = useParams<{ randmarSKU: string }>();
  const [loading, setLoading] = React.useState(true);
  const { applicationId, store } = props;
  const { productStore, snackStore } = store!;

  React.useEffect(() => {
    const fetchProduct = async () => {
      await productStore.getProduct(applicationId, randmarSKU || "");
      setLoading(false);
    }
    fetchProduct();
  }, [applicationId, randmarSKU, productStore]);

  const handleSwitchChange = async (propName: string, newValue: string) => {
    const { product } = productStore;
    const success = await productStore.setProductProperty(applicationId, product?.randmarSKU || "", propName, newValue);

    snackStore.showResult(success);
    await productStore.getProduct(applicationId, product?.randmarSKU || "");
    setLoading(false);
  }

  const { product } = productStore;

  if (!product) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-6 w-96" />
          </div>
          <Skeleton className="h-10 w-48" />
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="h-1">{loading && <Skeleton className="h-1 w-full" />}</div>

      <DocumentHeader justifyContent="space-between" alignItems="center" direction="row">
        <div>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="center" alignItems={{ xs: "left", md: "center" }}>
            <Typography variant="h1">{product.mpn}</Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="h2">{product.title}</Typography>
          </Stack>
        </div>
        <div hidden={!product.isManufacturer}>
          <Stack direction={{ xs: "column-reverse", md: "row" }} spacing={1} justifyContent="center" alignItems={{ xs: "left", md: "center" }}>
            <div className="flex items-center space-x-2 rounded-full border border-primary px-3 py-1 text-primary">
              <Label htmlFor="auto-update-switch" className="font-bold">Automatic Reorder</Label>
              <Switch
                id="auto-update-switch"
                checked={Boolean(product.autoUpdate)}
                onCheckedChange={(checked) => handleSwitchChange("AutoUpdate", String(checked))}
              />
            </div>
            <ProductActions product={product} />
          </Stack>
        </div>
      </DocumentHeader>

      <Accordion>
        <OpenOrdersSummary openOrders={product.activeOrderDetails} />
      </Accordion>

      <ProductOverviewPage
        applicationId={applicationId}
        sku={product.randmarSKU ?? ""}
      />

      <hr className="my-6" />

      {product.isManufacturer && product.manufacturerCategory &&
        <Accordion>
          <Paper>
            <MarginsGrid category={product.manufacturerCategory} distribution={product.distribution} />
          </Paper>
        </Accordion>
      }

      {product.isManufacturer &&
        <>
          <hr className="my-6" />
          <EditDistributionInformation product={product} />
        </>
      }

      {product.instantRebates && product.instantRebates.length > 0 &&
        <>
          <hr className="my-6" />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandPanelIcon />}>
              <StyledAccordionTitle>Instant Rebates</StyledAccordionTitle>
            </AccordionSummary >
            <StyledAccordionDetails>
              <Paper>
                <InstantRebatesGrid instantRebates={product.instantRebates} />
              </Paper>
            </StyledAccordionDetails>
          </Accordion>
        </>
      }
      
      {
        product.isManufacturer &&
        <>
          <hr className="my-6" />

          <Card>
            <CardTitle>Public listing</CardTitle>
            <CardContent>
              <EditShopifyListing product={product} applicationId={applicationId} />
            </CardContent>
          </Card>
        </>
      }
    </>
  );
}

export default withRoot(withManufacturer(inject("store")(observer(Product))));
